import React from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Paper,
  Checkbox,
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Skeleton,
} from '@mui/material';
import AppButton from '../AppButton/AppButton';
import {
  Search as SearchIcon,
  FilterAltOff as FilterAltOffIcon,
  Refresh as RefreshIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: 8,
  border: `1px solid ${theme.palette.divider}`,
  maxHeight: 'calc(100vh - 320px)',
  overflow: 'auto',
  width: '100%',
  '& .MuiTableHead-root': {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: theme.palette.background.paper,
  },
}));

const ToolbarContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 0',
  gap: 16,
}));

const SearchField = styled(TextField)(({ theme }) => ({
  minWidth: 300,
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.background.paper,
    '& fieldset': {
      borderColor: theme.palette.divider,
    },
  },
}));

const StyledTableRow = styled(TableRow)(({ theme, selected, disabled, muted }) => ({
  cursor: 'pointer',
  opacity: disabled ? 0.5 : muted ? 0.6 : 1,
  backgroundColor: selected ? `${theme.palette.primary.main}10` : 'inherit',
  '&:hover': {
    backgroundColor: selected
      ? `${theme.palette.primary.main}15`
      : theme.palette.action.hover,
  },
}));

const MuiDataTable = ({
  rows,
  headers,
  loading = false,
  selectable = true,
  /** When selectable, show header "select all" checkbox. Default true. */
  showSelectAll = true,
  onRowSelect,
  onSelectAll,
  selectedRows = [],
  sortable = true,
  sortInfo = [],
  onSort,
  searchable = true,
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search...',
  onClearFilters,
  showClearFilters = false,
  pagination = true,
  page = 0,
  rowsPerPage = 10,
  totalRows = 0,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [5, 10, 15, 25, 50],
  renderCell,
  getRowId = (row) => row.id,
  isRowDisabled = () => false,
  isRowMuted = () => false,
  emptyMessage = 'No data available',
  lastRefreshed,
  onRefresh,
  toolbarActions,
  stickyHeader = true,
  /** When true, no toolbar row (use an external toolbar above the table). */
  hideToolbar = false,
  /** Optional empty state (replaces default Typography when rows are empty). */
  emptyState,
  /** Passed to `<Table sx={...} />` (e.g. minWidth, tableLayout). */
  tableSx,
  /** Merged into the scrollable `TableContainer` (e.g. maxHeight). */
  tableContainerSx,
  /**
   * When true, the table grows to fill a flex parent (use with parent `flex: 1; minHeight: 0`
   * and `display: flex; flexDirection: column`) so the body scrolls inside `TableContainer`.
   */
  fillHeight = false,
  /** Optional title strip inside the card, above the scrollable table (e.g. View Config sections). */
  tableTitle,
  /** MUI `Table` size: `small` | `medium`. */
  tableSize = 'medium',
  /** Map of `header.key` → tooltip string; shows an info icon in the column header. */
  columnTooltips = {},
  /** Merged into column header info icon `sx` (e.g. `{ color: 'primary.main' }` for accent). */
  columnTooltipIconSx = {},
  /** When false, rows are not selection/click targets (read-only view). */
  rowClickable = true,
}) => {
  const renderColumnHeaderContent = (header) => {
    const tip = columnTooltips[header.key];
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
          {header.header}
          {header.isRequired && (
            <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>
              *
            </Box>
          )}
        </Box>
        {tip && (
          <Tooltip title={tip} arrow>
            <InfoOutlinedIcon
              sx={{
                fontSize: 16,
                color: 'text.secondary',
                cursor: 'help',
                flexShrink: 0,
                ...columnTooltipIconSx,
              }}
            />
          </Tooltip>
        )}
      </Box>
    );
  };

  const handleSelectAllClick = (event) => {
    if (onSelectAll) {
      onSelectAll(event.target.checked);
    }
  };

  const handleRowClick = (row) => {
    if (onRowSelect && !isRowDisabled(row)) {
      const rowId = getRowId(row);
      const isSelected = selectedRows.some((r) => getRowId(r) === rowId);
      onRowSelect(row, !isSelected);
    }
  };

  const handleSortClick = (column) => {
    if (onSort && sortable) {
      const currentSort = sortInfo.find((s) => s.column === column);
      const newDirection = currentSort?.direction === 'asc' ? 'desc' : 'asc';
      onSort(column, newDirection);
    }
  };

  const isSelected = (row) => {
    const rowId = getRowId(row);
    return selectedRows.some((r) => getRowId(r) === rowId);
  };

  const numSelected = selectedRows.length;
  const rowCount = rows.length;

  const renderSkeletonRows = () => {
    return Array.from({ length: rowsPerPage }).map((_, index) => (
      <TableRow key={`skeleton-${index}`}>
        {selectable && (
          <TableCell padding="checkbox">
            <Skeleton variant="rectangular" width={24} height={24} />
          </TableCell>
        )}
        {headers.map((header) => (
          <TableCell key={`skeleton-cell-${header.key}-${index}`}>
            <Skeleton variant="text" width="80%" />
          </TableCell>
        ))}
      </TableRow>
    ));
  };

  const showToolbar =
    !hideToolbar &&
    (searchable ||
      showClearFilters ||
      toolbarActions ||
      lastRefreshed ||
      onRefresh);

  const fillHeightRootSx = fillHeight
    ? {
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }
    : {};

  const fillHeightPaperSx = fillHeight
    ? {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }
    : {};

  const fillHeightTableContainerSx = fillHeight
    ? {
        flex: 1,
        minHeight: 0,
        maxHeight: 'none',
        overflow: 'auto',
      }
    : {};

  return (
    <Box sx={fillHeightRootSx}>
      {/* Toolbar */}
      {showToolbar && (
        <ToolbarContainer sx={fillHeight ? { flexShrink: 0 } : undefined}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {searchable && (
              <SearchField
                size="small"
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={(e) => onSearchChange?.(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            {showClearFilters && (
              <Tooltip title="Clear Filters">
                <IconButton onClick={onClearFilters} size="small">
                  <FilterAltOffIcon />
                </IconButton>
              </Tooltip>
            )}
            {toolbarActions}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {lastRefreshed && (
              <Typography variant="body2" color="text.secondary">
                Last updated: {lastRefreshed}
              </Typography>
            )}
            {onRefresh && (
              <AppButton
                variantType="secondary"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={onRefresh}
                sx={{ minWidth: 'auto', px: 2, py: 0.75 }}
              >
                Refresh
              </AppButton>
            )}
          </Box>
        </ToolbarContainer>
      )}

      {/* Table */}
      <Paper elevation={0} sx={fillHeightPaperSx}>
        {tableTitle && (
          <Box
            sx={{
              px: 2,
              py: 1.5,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              backgroundColor: (theme) => theme.palette.grey[50],
            }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {tableTitle}
            </Typography>
          </Box>
        )}
        <StyledTableContainer
          sx={{
            ...tableContainerSx,
            ...fillHeightTableContainerSx,
          }}
        >
          <Table
            stickyHeader={stickyHeader}
            size={tableSize}
            sx={{
              tableLayout: 'fixed',
              width: '100%',
              ...tableSx,
            }}
          >
            <TableHead>
              <TableRow>
                {selectable && (
                  <TableCell padding="checkbox" sx={{ width: 48 }}>
                    {showSelectAll && (
                      <Checkbox
                        indeterminate={numSelected > 0 && numSelected < rowCount}
                        checked={rowCount > 0 && numSelected === rowCount}
                        onChange={handleSelectAllClick}
                        color="primary"
                        inputProps={{ 'aria-label': 'Select all rows' }}
                      />
                    )}
                  </TableCell>
                )}
                {headers.map((header) => (
                  <TableCell
                    key={header.key}
                    align={header.align || 'left'}
                    sx={{
                      fontWeight: 600,
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      whiteSpace: 'nowrap',
                      minWidth: header.minWidth,
                      maxWidth: header.maxWidth,
                      width: header.width,
                      verticalAlign: 'bottom',
                    }}
                    sortDirection={
                      sortInfo.find((s) => s.column === header.key)?.direction ||
                      false
                    }
                  >
                    {sortable && header.sortable !== false ? (
                      <TableSortLabel
                        active={sortInfo.some((s) => s.column === header.key)}
                        direction={
                          sortInfo.find((s) => s.column === header.key)
                            ?.direction || 'asc'
                        }
                        onClick={() => handleSortClick(header.key)}
                      >
                        {renderColumnHeaderContent(header)}
                      </TableSortLabel>
                    ) : (
                      renderColumnHeaderContent(header)
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                renderSkeletonRows()
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={headers.length + (selectable ? 1 : 0)}
                    align="center"
                    sx={{ py: 8 }}
                  >
                    {emptyState ?? (
                      <Typography variant="body2" color="text.secondary">
                        {emptyMessage}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const rowId = getRowId(row);
                  const isItemSelected = isSelected(row);
                  const isDisabled = isRowDisabled(row);
                  const isMuted = isRowMuted(row);

                  const cells = (
                    <>
                      {selectable && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isItemSelected}
                            disabled={isDisabled}
                            color="primary"
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => handleRowClick(row)}
                          />
                        </TableCell>
                      )}
                      {headers.map((header) => (
                        <TableCell
                          key={`${rowId}-${header.key}`}
                          align={header.align || 'left'}
                          sx={{
                            verticalAlign: 'top',
                            minWidth: header.minWidth,
                            maxWidth: header.maxWidth,
                            width: header.width,
                            ...(header.noWrap
                              ? {
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }
                              : {
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                  overflowWrap: 'anywhere',
                                }),
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              display: 'block',
                              maxWidth: '100%',
                              ...(header.noWrap
                                ? {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }
                                : {}),
                            }}
                          >
                            {renderCell
                              ? renderCell(row, header.key, row[header.key])
                              : row[header.key]}
                          </Box>
                        </TableCell>
                      ))}
                    </>
                  );

                  if (!rowClickable) {
                    return (
                      <TableRow key={rowId} hover>
                        {cells}
                      </TableRow>
                    );
                  }

                  return (
                    <StyledTableRow
                      key={rowId}
                      selected={isItemSelected}
                      disabled={isDisabled}
                      muted={isMuted}
                      onClick={() => handleRowClick(row)}
                      role={selectable ? 'checkbox' : undefined}
                      aria-checked={selectable ? isItemSelected : undefined}
                    >
                      {cells}
                    </StyledTableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>

        {/* Pagination */}
        {pagination && !loading && rows.length > 0 && (
          <TablePagination
            component="div"
            count={totalRows}
            page={page}
            onPageChange={(e, newPage) => onPageChange?.(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) =>
              onRowsPerPageChange?.(parseInt(e.target.value, 10))
            }
            rowsPerPageOptions={rowsPerPageOptions}
            sx={{
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
              ...(fillHeight && { flexShrink: 0 }),
            }}
          />
        )}
      </Paper>
    </Box>
  );
};

MuiDataTable.propTypes = {
  rows: PropTypes.array.isRequired,
  headers: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      header: PropTypes.string.isRequired,
      align: PropTypes.oneOf(['left', 'center', 'right', 'inherit', 'justify']),
      sortable: PropTypes.bool,
      minWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      wrap: PropTypes.bool,
      isRequired: PropTypes.bool,
      /** When true, keep on one line with ellipsis (IDs, codes). Default: wrap long text. */
      noWrap: PropTypes.bool,
    })
  ).isRequired,
  loading: PropTypes.bool,
  selectable: PropTypes.bool,
  showSelectAll: PropTypes.bool,
  onRowSelect: PropTypes.func,
  onSelectAll: PropTypes.func,
  selectedRows: PropTypes.array,
  sortable: PropTypes.bool,
  sortInfo: PropTypes.array,
  onSort: PropTypes.func,
  searchable: PropTypes.bool,
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  onClearFilters: PropTypes.func,
  showClearFilters: PropTypes.bool,
  pagination: PropTypes.bool,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  totalRows: PropTypes.number,
  onPageChange: PropTypes.func,
  onRowsPerPageChange: PropTypes.func,
  rowsPerPageOptions: PropTypes.array,
  renderCell: PropTypes.func,
  getRowId: PropTypes.func,
  isRowDisabled: PropTypes.func,
  isRowMuted: PropTypes.func,
  emptyMessage: PropTypes.string,
  lastRefreshed: PropTypes.string,
  onRefresh: PropTypes.func,
  toolbarActions: PropTypes.node,
  stickyHeader: PropTypes.bool,
  hideToolbar: PropTypes.bool,
  emptyState: PropTypes.node,
  tableSx: PropTypes.object,
  tableContainerSx: PropTypes.object,
  fillHeight: PropTypes.bool,
  tableTitle: PropTypes.string,
  tableSize: PropTypes.oneOf(['small', 'medium']),
  columnTooltips: PropTypes.object,
  columnTooltipIconSx: PropTypes.object,
  rowClickable: PropTypes.bool,
};

export default MuiDataTable;
